import React, { useState } from "react";
import { Grid, TextField, Autocomplete, Box, IconButton, Typography } from "@mui/material";
import { idCardTypes } from "../../data/idCardData";
import CryptoJS from "crypto-js";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import { extractAiTextFromImage } from "../../services/aiOcrService";
import { parseKeyValueText, firstFrom, pickDate } from "../../services/ocrUtils";

// Encryption/Decryption Key - In production, this should be stored securely
const ENCRYPTION_KEY =  "MoonaConsultancySecretKey";

/**
 * Encrypt sensitive data
 * @param {string} text - Text to encrypt
 * @returns {string} - Encrypted text
 */
export const encryptData = (text) => {
  if (!text || text.trim() === "") return "";
  try {
    return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    return text;
  }
};

/**
 * Decrypt sensitive data
 * @param {string} encryptedText - Encrypted text to decrypt
 * @returns {string} - Decrypted text
 */
export const decryptData = (encryptedText) => {
  if (!encryptedText || encryptedText.trim() === "") return "";
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return decrypted || encryptedText;
  } catch (error) {
    console.error("Decryption error:", error);
    return encryptedText;
  }
};

const IDCardFields = ({
  formData,
  handleChange,
  section = 1,
  textInputStyle = {},
  fieldErrors = {},
}) => {
  const [ocrBusy1, setOcrBusy1] = useState(false);
  const [ocrBusy2, setOcrBusy2] = useState(false);
  const idTypeError = fieldErrors[`idtype${section}`] || "";
  const normalizeIdTypeLabel = (raw) => {
    if (!raw) return "";
    const t = raw.toString().toLowerCase();
    if (/(aadhaar|aadhar|uidai)/i.test(t)) return "Aadhaar Card";
    if (/(pan|permanent\s*account\s*number|income\s*tax)/i.test(t)) return "PAN Card";
    if (/(driv|licen[sc]e|dl\s?no|transport\s*dept|rto)/i.test(t)) return "Driving License";
    if (/(passport|republic\s*of\s*india)/i.test(t)) return "Passport";
    if (/(voter|epic|election\s*commission)/i.test(t)) return "Voter ID Card";
    if (/(apaar|academic\s*bank\s*of\s*credits)/i.test(t)) return "APAAR ID";
    if (/(student)/i.test(t)) return "Student ID Card";
    if (/(employee|staff)/i.test(t)) return "Employee ID Card";
    if (/(ration)/i.test(t)) return "Ration Card";
    return raw;
  };

  const detectIdTypeFromContent = (text) => {
    if (!text) return "";
    const t = text.toLowerCase();
    // Heuristic patterns
    const aadhaarNum = /(\b\d{4}\s\d{4}\s\d{4}\b|\b\d{12}\b)/;
    const panNum = /\b([A-Z]{5}[0-9]{4}[A-Z])\b/i;
    const passportNum = /\b([A-PR-WYa-pr-wy][0-9]{7})\b/; // excludes O and I
    const voterNum = /\b([A-Z]{3}[0-9]{7})\b/i; // EPIC format
    const dlHints = /(driv|licen[sc]e|dl\s?no|transport\s*dept|rto)/i;

    if (/(aadhaar|aadhar|uidai)/i.test(t) || aadhaarNum.test(text)) return "Aadhaar Card";
    if (/(pan|permanent\s*account\s*number|income\s*tax)/i.test(t) || panNum.test(text)) return "PAN Card";
    if (/(passport|republic\s*of\s*india)/i.test(t) || passportNum.test(text)) return "Passport";
    if (/(voter|epic|election\s*commission)/i.test(t) || voterNum.test(text)) return "Voter ID Card";
    if (dlHints.test(t)) return "Driving License";
    if (/(apaar|academic\s*bank\s*of\s*credits)/i.test(t)) return "APAAR ID";
    if (/(student)/i.test(t)) return "Student ID Card";
    if (/(employee|staff)/i.test(t)) return "Employee ID Card";
    if (/(ration)/i.test(t)) return "Ration Card";
    return "";
  };

  const autoFillFromOcr = async (file) => {
    try {
      const text = await extractAiTextFromImage(file);
      const kv = parseKeyValueText(text);
      // Prefer explicit key if present, else detect from full text
      const rawType = firstFrom(kv, ["idtype", "type", "doctype"]) || detectIdTypeFromContent(text);
      const mappedType = normalizeIdTypeLabel(rawType);
      const updates = {
        [`idtype${section}`]: mappedType || formData[`idtype${section}`] || "",
        [`idcardname${section}`]: firstFrom(kv, ["name", "fullname", "holdername", "idname"]) || formData[`idcardname${section}`] || "",
        [`idnumber${section}`]: firstFrom(kv, ["idnumber", "idno", "number", "aadhaar", "pan", "dlno"]) || formData[`idnumber${section}`] || "",
        [`issuedate${section}`]: pickDate(firstFrom(kv, ["issuedate", "dateofissue", "doi", "issue"])) || formData[`issuedate${section}`] || "",
        [`expirydate${section}`]: pickDate(firstFrom(kv, ["expirydate", "validtill", "validupto", "doe"])) || formData[`expirydate${section}`] || "",
      };
      for (const [name, value] of Object.entries(updates)) {
        if (value) handleChange({ target: { name, value } });
      }
    } catch (e) {
      console.warn("IDCardFields OCR failed:", e?.message || e);
    }
  };
  const nameError = fieldErrors[`idcardname${section}`] || "";
  const idNumberError = fieldErrors[`idnumber${section}`] || "";

  // Helper to handle encrypted field changes
  const handleEncryptedChange = (fieldName, value) => {
    handleChange({ target: { name: fieldName, value } });
  };


  return (
    <Grid container spacing={3}>
      {/* Upload Images - moved to top */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Front Page Image
        </label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id={`image1-upload-${section}`}
            type="file"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  handleChange({
                    target: {
                      name: `image1${section}`,
                      value: reader.result,
                    },
                  });
                };
                reader.readAsDataURL(file);
                setOcrBusy1(true);
                autoFillFromOcr(file).finally(() => setOcrBusy1(false));
              }
            }}
          />
          <label htmlFor={`image1-upload-${section}`}>
            <MDButton
              variant="outlined"
              color="info"
              component="span"
              size="small"
              startIcon={<Icon>upload</Icon>}
            >
              Upload Front Page
            </MDButton>
          </label>
          <Typography variant="caption" color="text.secondary">
            {ocrBusy1 ? "Reading text to auto-fill…" : "Upload image to auto fill"}
          </Typography>
          {formData[`image1${section}`] && (
            <Box
              sx={{
                position: "relative",
                mt: 2,
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Preview:
              </Typography>
              <img
                src={formData[`image1${section}`]}
                alt="Front page preview"
                style={{
                  width: "100%",
                  maxHeight: "150px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  backgroundColor: "white",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: 1,
                  "&:hover": { backgroundColor: "white" },
                }}
                onClick={() => {
                  handleChange({ target: { name: `image1${section}`, value: "" } });
                  const fileInput = document.getElementById(`image1-upload-${section}`);
                  if (fileInput) fileInput.value = "";
                }}
              >
                <Icon fontSize="small" color="error">
                  close
                </Icon>
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Back Page Image
        </label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id={`image2-upload-${section}`}
            type="file"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  handleChange({
                    target: {
                      name: `image2${section}`,
                      value: reader.result,
                    },
                  });
                };
                reader.readAsDataURL(file);
                setOcrBusy2(true);
                autoFillFromOcr(file).finally(() => setOcrBusy2(false));
              }
            }}
          />
          <label htmlFor={`image2-upload-${section}`}>
            <MDButton
              variant="outlined"
              color="info"
              component="span"
              size="small"
              startIcon={<Icon>upload</Icon>}
            >
              Upload Back Page
            </MDButton>
          </label>
          <Typography variant="caption" color="text.secondary">
            {ocrBusy2 ? "Reading text to auto-fill…" : "Upload image to auto fill"}
          </Typography>
          {formData[`image2${section}`] && (
            <Box
              sx={{
                position: "relative",
                mt: 2,
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Preview:
              </Typography>
              <img
                src={formData[`image2${section}`]}
                alt="Back page preview"
                style={{
                  width: "100%",
                  maxHeight: "150px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  backgroundColor: "white",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: 1,
                  "&:hover": { backgroundColor: "white" },
                }}
                onClick={() => {
                  handleChange({ target: { name: `image2${section}`, value: "" } });
                  const fileInput = document.getElementById(`image2-upload-${section}`);
                  if (fileInput) fileInput.value = "";
                }}
              >
                <Icon fontSize="small" color="error">
                  close
                </Icon>
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>
      {/* ID Card Type */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          ID Card Type <span className="text-red-500">*</span>
        </label>
        <Autocomplete
          freeSolo
          options={idCardTypes}
          value={formData[`idtype${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `idtype${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select or enter ID card type"
              sx={textInputStyle}
              error={!!idTypeError}
              helperText={idTypeError}
            />
          )}
        />
      </Grid>

      {/* Name on ID Card */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name on ID Card <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`idcardname${section}`}
          value={formData[`idcardname${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter name as on ID card"
          sx={textInputStyle}
          error={!!nameError}
          helperText={nameError}
        />
      </Grid>

      {/* ID Number (Encrypted) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          ID Number <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`idnumber${section}`}
          value={formData[`idnumber${section}`] || ""}
          onChange={(e) => handleEncryptedChange(`idnumber${section}`, e.target.value)}
          variant="outlined"
          placeholder="Enter ID number"
          sx={textInputStyle}
          error={!!idNumberError}
          helperText={idNumberError}
        />
      </Grid>

      {/* Issue Date (Encrypted) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Issue Date 
        </label>
        <TextField
          fullWidth
          type="date"
          name={`issuedate${section}`}
          value={formData[`issuedate${section}`] || ""}
          onChange={(e) => handleEncryptedChange(`issuedate${section}`, e.target.value)}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={textInputStyle}
        />
      </Grid>

      {/* Expiry Date (Encrypted) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Expiry Date
        </label>
        <TextField
          fullWidth
          type="date"
          name={`expirydate${section}`}
          value={formData[`expirydate${section}`] || ""}
          onChange={(e) => handleEncryptedChange(`expirydate${section}`, e.target.value)}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={textInputStyle}
        />
      </Grid>

      {/* Additional Notes/Description */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Additional Notes
        </label>
        <TextField
          fullWidth
          name={`idcarddescription${section}`}
          value={formData[`idcarddescription${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Any additional notes or comments"
          multiline
          rows={1}
          sx={textInputStyle}
        />
      </Grid>

    </Grid>
  );
};

export default IDCardFields;
